import { IF, NO, UIDialogView, UITextView, UIView, YES } from "uicore-ts"
import { CBDataViewCellDescriptorWithObject } from "../Custom components/CB/CBDataView"
import { RHCore } from "../Core/RHCore"
import { RHFocusManager } from "../Custom components/RH/Keyboard navigation/RHFocusManager"
import { RHPageFocusManager } from "../Custom components/RH/Keyboard navigation/RHPageFocusManager"
import { RHButton } from "../Custom components/RH/RHButton"
import { RHButtons, RHColors, RHDialogs, RHSpacing } from "../Custom components/RH/Utils_slop/RHDesignTokens"
import { RHTextSanitizer } from "../Custom components/RH/Utils_slop/RHTextSanitizer"
import { RHInvitedCompaniesViewTokens } from "../Custom components/RH/Utils_slop/RHViewTokens"
import { InvitedCompaniesView } from "../NewProcurement/InvitedCompaniesView"
import { InvitedCompany, RHProcurement } from "../SocketClientFunctions"


class ProcurementInvitationCompaniesView extends InvitedCompaniesView {
    availableHeight = RHInvitedCompaniesViewTokens.companiesTableHeight
    heightReservedOutsideCompaniesView = 0
    _invitedCompanyKeys = new Set<string>()

    override _focusManager = new RHFocusManager(() => [
        this.companiesDataView.searchTextField,
        this.addNameTextField, this.addRegcodeTextField, this.addEmailTextField, this.addCompanyButton
    ])

    constructor() {
        super()
        this.setupDescriptors()
        this.companiesDataView.descriptors = [
            ...this._sharedCompanyDescriptors("management").filter(descriptor =>
                descriptor.keyPath === "isInvited" || descriptor.keyPath === "name"
            ).map(descriptor => ({
                ...descriptor,
                title: IF(descriptor.keyPath === "name")(() => "Ettevõte").ELSE(() => ""),
                titleLabelAlignment: UITextView.textAlignment.left
            })),
            CBDataViewCellDescriptorWithObject<InvitedCompany, UITextView>({
                title: "Olek", keyPath: "registryCode", weight: 1,
                initCustomView: () => new UITextView().configuredWithObject({ isSingleLine: NO }),
                updateCell: (cellView, company) => {
                    cellView.customView.text = this.invitationStatusTextForCompany(company)
                }
            })
        ]
        for (const control of [
            this.sourceControlsLabel, this.statisticsSourceCheckbox, this.companyInterestSourceCheckbox,
            this.manuallyAddedSourceCheckbox, this.removeAllManuallyAddedCompaniesButton,
            this.importSectionLabel, this.importTextArea, this.importCompaniesButton
        ]) {
            control.removeFromSuperview()
        }
        this.addSectionLabel.configureWithObject({
            text: "Otsi ettevõtet nime või registrikoodi järgi või sisesta andmed käsitsi",
            isSingleLine: NO
        })
    }

    async loadInvitationChoicesWithExistingInvitations(existingInvitations: InvitedCompany[]): Promise<void> {
        const invitedCompanies = existingInvitations.filter(company => company.isInvited)
        this._invitedCompanyKeys = new Set(invitedCompanies
            .map(company => this._identityKeyForCompany(company)))
        await this.loadWithCompanies(invitedCompanies.map(company => ({
            ...company, isInvited: NO
        })), YES)
    }

    invitationStatusTextForCompany(company: InvitedCompany): string {
        if (this._invitedCompanyKeys.has(this._identityKeyForCompany(company))) {
            return "Kutsutud"
        }
        return "Ei ole kutsutud"
    }

    override layoutSubviews() {
        super.layoutSubviews()
        const bounds = this.contentBounds
        const addFieldRowCount = IF(bounds.width >= RHSpacing.medium * 45)(() => 1).ELSE(() => 4)
        const heightReservedForAdding = RHSpacing.medium + this.addSectionLabel.intrinsicContentHeight(bounds.width) +
            addFieldRowCount * (RHSpacing.small + RHButtons.compactHeight)
        const maximumTableHeight = Math.max(
            RHSpacing.medium * 14,
            this.availableHeight - heightReservedForAdding
        )
        this.companiesDataView.maximumIntrinsicContentHeight = Math.min(RHSpacing.medium * 35, maximumTableHeight)
        const tableHeight = Math.min(
            this.companiesDataView.maximumIntrinsicContentHeight,
            this.companiesDataView.intrinsicContentHeight(bounds.width) * 1.25
        )
        const dialogHeight = tableHeight + heightReservedForAdding + this.heightReservedOutsideCompaniesView
        bounds
            .rectangleWithHeight(Math.min(maximumTableHeight, tableHeight + dialogHeight * 0.25))
            .assignedAsFrameOfView(this.companiesDataView)
            .rectangleForNextRow(RHSpacing.medium, this.addSectionLabel)
            .assignedAsFrameOfView(this.addSectionLabel)
            .IF(bounds.width >= RHSpacing.medium * 45)
            .rectangleForNextRow(RHSpacing.small, RHButtons.compactHeight)
            .distributeViewsAlongWidth(
                [this.addNameTextField, this.addRegcodeTextField, this.addEmailTextField, this.addCompanyButton],
                [3, 1.5, 2, 1],
                RHSpacing.medium
            )
            .ELSE()
            .rectangleForNextRow(RHSpacing.small, RHButtons.compactHeight)
            .assignedAsFrameOfView(this.addNameTextField)
            .rectangleForNextRow(RHSpacing.small, RHButtons.compactHeight)
            .assignedAsFrameOfView(this.addRegcodeTextField)
            .rectangleForNextRow(RHSpacing.small, RHButtons.compactHeight)
            .assignedAsFrameOfView(this.addEmailTextField)
            .rectangleForNextRow(RHSpacing.small, RHButtons.compactHeight)
            .assignedAsFrameOfView(this.addCompanyButton)
            .ENDIF()
    }
}


export class InviteCompaniesToProcurementDialogView extends UIDialogView {
    readonly contentView = new UIView().configuredWithObject({
        backgroundColor: RHDialogs.share.dialogBackgroundColor,
        style: {
            borderRadius: RHDialogs.share.dialogBorderRadius,
            boxShadow: RHDialogs.share.dialogBoxShadow
        }
    }).addedAsSubviewToView(this.view)

    readonly titleLabel = new UITextView(undefined, UITextView.type.header4)
        .configuredWithObject({ text: "Kutsu ettevõtteid hankele", isSingleLine: NO }).addedAsSubviewToView(this.contentView)
    readonly explanationLabel = new UITextView().configuredWithObject({
        text: "Kutse kehtib kogu hankele. Ligipääsu saab ettevõte; e-post on ainult teavituseks. " +
            "Juba kutsutud ettevõtte valimisel saadetakse kutsekiri uuesti.",
        isSingleLine: NO, textColor: RHColors.textSecondary
    }).addedAsSubviewToView(this.contentView)
    readonly companiesView = new ProcurementInvitationCompaniesView().addedAsSubviewToView(this.contentView)
    readonly resultLabel = new UITextView().configuredWithObject({
        isSingleLine: NO, style: { whiteSpace: "pre-wrap" }
    }).addedAsSubviewToView(this.contentView)
    readonly sendButton = new RHButton().configuredWithObject({
        titleLabel: { text: "Kutsun" }, variant: RHInvitedCompaniesViewTokens.dialogConfirmButtonVariant,
        controlEventTargetAccumulator: { PrimaryActionTriggered: () => { void this._sendInvitations() } }
    }).addedAsSubviewToView(this.contentView)
    readonly closeButton = new RHButton().configuredWithObject({
        titleLabel: {
            text: RHButtons.dialogCloseTitle,
            style: { fontSize: RHButtons.dialogCloseFontSize, lineHeight: RHButtons.dialogCloseLineHeight }
        },
        variant: RHButtons.dialogCloseVariant,
        contentPadding: RHButtons.dialogCloseContentPadding,
        style: { borderRadius: RHButtons.dialogCloseBorderRadius },
        hoverText: "Sulge",
        controlEventTargetAccumulator: { PrimaryActionTriggered: () => this.dismiss() }
    }).addedAsSubviewToView(this.contentView)
    readonly bottomSpacerView = new UIView().configuredWithObject({ userInteractionEnabled: NO })
        .addedAsSubviewToView(this)

    _procurement?: RHProcurement
    _invitingCompanyId?: string
    _isSending = NO
    _isDismissed = NO
    _sendButtonUpdateTimer?: ReturnType<typeof setInterval>
    _hasFocusLayer = NO
    override _focusManager = new RHFocusManager(() => [
        this.companiesView, this.sendButton, this.closeButton
    ])

    constructor() {
        super()
        this.view = this.contentView
        this.closeButton.viewHTMLElement.setAttribute("aria-label", "Sulge")
        this.companiesView.companiesDidChange = () => this._updateSendButtonAvailability()
    }

    async showForProcurement(procurement: RHProcurement): Promise<void> {
        this._isDismissed = NO
        this._procurement = procurement
        this._invitingCompanyId = RHCore.sharedInstance.companyAffiliationStore.activeCompanyId
        this.resultLabel.text = ""
        await this.companiesView.loadInvitationChoicesWithExistingInvitations(procurement.invitedCompanies ?? [])
        if (this._isDismissed) {
            return
        }
        this._updateSendButtonAvailability()
        this.showInRootView(YES)
    }

    override willAppear(animated?: boolean) {
        super.willAppear(animated)
        RHPageFocusManager.shared.pushManagerAsNewLayer(this._focusManager)
        this._hasFocusLayer = YES
        this._sendButtonUpdateTimer = setInterval(() => this._updateSendButtonAvailability(), 1000)
    }

    override dismiss(animated?: boolean) {
        super.dismiss(animated)
        this._isDismissed = YES
        if (this._hasFocusLayer) {
            RHPageFocusManager.shared.popLayer()
            this._hasFocusLayer = NO
        }
        clearInterval(this._sendButtonUpdateTimer)
        this._sendButtonUpdateTimer = undefined
    }

    _updateSendButtonAvailability(): void {
        const deadlineDate = this._procurement?.deadlineDate
        const canInvite = !!deadlineDate && new Date(deadlineDate).getTime() > Date.now() &&
            this._invitingCompanyId === RHCore.sharedInstance.companyAffiliationStore.activeCompanyId
        this.sendButton.enabled = canInvite && !this._isSending && this.companiesView.invitedCompanies.length > 0
        this.sendButton.hoverText = ""
        if (!canInvite) {
            this.sendButton.hoverText = "Kutsumiseks peab pakkumuste esitamise tähtaeg olema tulevikus ja valitud sama hankija ettevõte."
        }
    }

    async _sendInvitations(): Promise<void> {
        this._updateSendButtonAvailability()
        const procurement = this._procurement
        if (!procurement?._id || !this.sendButton.enabled || this._isSending) {
            return
        }
        const companies = this.companiesView.invitedCompanies.map(company => ({
            _id: company._id, name: company.name, registryCode: company.registryCode, email: company.email
        }))
        this._isSending = YES
        this.companiesView.userInteractionEnabled = NO
        this.closeButton.enabled = NO
        this._updateSendButtonAvailability()
        try {
            const result = await RHCore.sharedInstance.procurementStore.inviteCompaniesToProcurementWithInput({
                procurementId: procurement._id.toString(), companies,
                expectedPublishedVersion: procurement.publishedVersion, expectedUpdatedAt: procurement.updatedAt
            })
            this._procurement = result.procurement
            await this.companiesView.loadInvitationChoicesWithExistingInvitations(result.procurement.invitedCompanies ?? [])
            this.resultLabel.text = `${result.addedCompanyCount} ettevõtet lisati kutsutute hulka. ` +
                `${result.sentEmailCount} kutsekirja saadeti.`
            if (result.failedEmails.length > 0) {
                const failedEmailAddresses = new Set(result.failedEmails)
                await this.companiesView.loadWithCompanies(this.companiesView.allCompanies.map(company => ({
                    ...company, isInvited: failedEmailAddresses.has(company.email?.trim().toLowerCase() ?? "")
                })), YES)
                this.resultLabel.text += "\nKutsekirja saatmine ebaõnnestus: " +
                    result.failedEmails.map(email => RHTextSanitizer.escapedHTML(email)).join(", ") +
                    ". Ettevõtete kutsed on salvestatud. Saad valitud ettevõtetele kirjad uuesti saata."
            }
        } catch (error) {
            this.resultLabel.text = "Kutsete saatmine ebaõnnestus. Värskenda vaadet enne uuesti proovimist."
            if (error instanceof Error) {
                this.resultLabel.text = RHTextSanitizer.escapedHTML(error.message)
            }
        } finally {
            this._isSending = NO
            this.companiesView.userInteractionEnabled = YES
            this.closeButton.enabled = YES
            this._updateSendButtonAvailability()
            this.setNeedsLayoutUpToRootView()
        }
    }

    override layoutSubviews() {
        super.layoutSubviews()
        const bounds = this.contentBounds
        const padding = RHSpacing.medium
        const dialogWidth = Math.min(padding * 70, bounds.width * 0.95)
        this.contentView.frame = bounds.rectangleWithWidth(dialogWidth, 0.5).rectangleWithHeight(0)
        const contentBounds = this.contentView.contentBoundsWithInset(padding * 1.5)
        const titleWidth = contentBounds.width - RHButtons.dialogCloseSize - padding
        const headerFrame = contentBounds
            .rectangleWithHeight(Math.max(padding * 3, this.titleLabel.intrinsicContentHeight(titleWidth)))
        headerFrame
            .rectangleWithWidth(titleWidth)
            .assignedAsFrameOfView(this.titleLabel)
            .rectangleForNextColumn(padding, RHButtons.dialogCloseSize)
            .rectangleWithHeight(RHButtons.dialogCloseSize, 0.5)
            .assignedAsFrameOfView(this.closeButton)
        const explanationFrame = headerFrame
            .rectangleForNextRow(RHSpacing.small, this.explanationLabel)
            .assignedAsFrameOfView(this.explanationLabel)
        const resultHeight = IF(!!this.resultLabel.text)(() =>
            padding + this.resultLabel.intrinsicContentHeight(contentBounds.width)
        ).ELSE(() => 0)
        const maximumDialogHeight = window.innerHeight / UIView.pageScale - padding * 2
        const heightReservedBelowCompanies = resultHeight + padding + RHButtons.compactHeight + padding * 1.5
        const heightReservedOutsideCompaniesView = explanationFrame.max.y + padding + heightReservedBelowCompanies
        this.companiesView.configureWithObject({
            availableHeight: maximumDialogHeight - heightReservedOutsideCompaniesView,
            heightReservedOutsideCompaniesView
        })
        const footerFrame = explanationFrame
            .rectangleForNextRow(RHSpacing.medium, this.companiesView)
            .assignedAsFrameOfView(this.companiesView)
            .IF(!!this.resultLabel.text)
            .rectangleForNextRow(padding, this.resultLabel)
            .assignedAsFrameOfView(this.resultLabel)
            .ENDIF()
            .rectangleForNextRow(padding, RHButtons.compactHeight)
            .rectangleWithWidth(padding * 10, 0.5)
            .assignedAsFrameOfView(this.sendButton)
        const dialogFrame = bounds.rectangleWithWidth(dialogWidth, 0.5)
            .rectangleWithHeight(footerFrame.max.y + padding * 1.5, 0.5)
        this.contentView.frame = dialogFrame.rectangleWithY(Math.max(padding, dialogFrame.y))
        this.bottomSpacerView.frame = bounds.rectangleWithHeight(this.contentView.frame.max.y + padding)
    }
}
